import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import MaintenancePage from './components/MaintenancePage';
import Loader from './components/Loader';
import ErrorBoundary from './components/ErrorBoundary';
import { checkStrapiHealth } from './utils/strapiHealth';
import './App.css';
import './i18n';

// Lazy load pages for better performance
const WFCNikeHome = lazy(() => import('./pages/WFCNikeHome'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage_elegant'));
const MatchesPage = lazy(() => import('./pages/MatchesPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));
const PlayerDetailPage = lazy(() => import('./pages/PlayerDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function App() {
  const [isStrapiDown, setIsStrapiDown] = useState(false);
  const [isCheckingStrapi, setIsCheckingStrapi] = useState(true);

  useEffect(() => {
    // Check Strapi health on mount
    const checkHealth = async () => {
      const isHealthy = await checkStrapiHealth();
      setIsStrapiDown(!isHealthy);
      setIsCheckingStrapi(false);
    };

    checkHealth();

    // Recheck Strapi health every 30 seconds
    const interval = setInterval(checkHealth, 30000);

    return () => clearInterval(interval);
  }, []);

  // Show loading or maintenance page while checking
  if (isCheckingStrapi) {
    return (
      <HelmetProvider>
        <Loader />
      </HelmetProvider>
    );
  }

  // Show maintenance page if Strapi is down
  if (isStrapiDown) {
    return (
      <HelmetProvider>
        <MaintenancePage />
      </HelmetProvider>
    );
  }

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <div className="App">
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route path="/" element={<WFCNikeHome />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/matches" element={<MatchesPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:documentId" element={<NewsDetailPage />} />
                <Route path="/player/:slug" element={<PlayerDetailPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </div>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
